/**
 * Staff order — pure helpers shared by the staff page and the HR tab.
 *
 * Money is integer cents everywhere (the API's unit); these turn it into the
 * "R1,034.94" the pages show, keep the order maths in one place, and render
 * the API's datetimes in South African time whatever the viewer's machine
 * is set to.
 */
import { parseApiDate } from '@/lib/apiDate'
import type {
  StaffOrderCampus,
  StaffOrderItem,
  StaffOrderLine,
  StaffOrderSize,
} from '@/services/api/staffOrderService'

/** Most of one item in one size a person can order. */
export const MAX_QUANTITY = 10

export const SAST_TIME_ZONE = 'Africa/Johannesburg'

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

/**
 * "R1,034.94": always two decimals, comma thousands, no space after the R.
 * Built by hand rather than with Intl: the en-ZA locale groups with spaces
 * and uses a decimal comma, which is not how the order shows money.
 */
export function formatRand(cents: number): string {
  const value = Number.isFinite(cents) ? Math.round(cents) : 0
  const sign = value < 0 ? '-' : ''
  const abs = Math.abs(value)
  const rands = String(Math.floor(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return `${sign}R${rands}.${String(abs % 100).padStart(2, '0')}`
}

/**
 * An amount typed in Rands — "1200", "1200.00", "R1 200", "1,034.94" or
 * "310,44" — as cents. Null when it is not a non-negative amount with at most
 * two decimals. A lone comma followed by one or two digits is a decimal
 * comma; any other comma groups thousands.
 */
export function randsToCents(text: string): number | null {
  let s = (text ?? '').trim().replace(/^R/i, '').replace(/\s/g, '') // \s also covers a no-break space
  if (!s) return null
  if (s.includes(',')) {
    const commas = (s.match(/,/g) || []).length
    s = !s.includes('.') && commas === 1 && /,\d{1,2}$/.test(s)
      ? s.replace(',', '.')
      : s.replace(/,/g, '')
  }
  const m = /^(\d+)(?:\.(\d{1,2}))?$/.exec(s)
  if (!m) return null
  return Number(m[1]) * 100 + (m[2] ? Number(m[2].padEnd(2, '0')) : 0)
}

/** Cents as the plain Rands an input shows: 103494 → "1034.94". */
export function centsToRandsInput(cents: number): string {
  const abs = Math.abs(Math.round(cents))
  return `${cents < 0 ? '-' : ''}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`
}

// ---------------------------------------------------------------------------
// Prices
// ---------------------------------------------------------------------------

/** The price of one size of an item, or null when the item has no such size. */
export function sizePrice(item: Pick<StaffOrderItem, 'sizes'>, size: string): number | null {
  const found = item.sizes.find((s) => s.size === size)
  return found ? found.price_cents : null
}

/** True when the item's sizes do not all cost the same. */
export function pricesDiffer(item: Pick<StaffOrderItem, 'sizes'>): boolean {
  return new Set(item.sizes.map((s) => s.price_cents)).size > 1
}

/** "R310.44", or "From R1,034.94" when sizes differ in price. Empty without sizes. */
export function priceLabel(item: Pick<StaffOrderItem, 'sizes'>): string {
  if (item.sizes.length === 0) return ''
  const lowest = Math.min(...item.sizes.map((s) => s.price_cents))
  return pricesDiffer(item) ? `From ${formatRand(lowest)}` : formatRand(lowest)
}

/** A size option: "XL — R1,099.00" when sizes differ in price, else just "XL". */
export function sizeOptionLabel(item: Pick<StaffOrderItem, 'sizes'>, size: StaffOrderSize): string {
  return pricesDiffer(item) ? `${size.size} — ${formatRand(size.price_cents)}` : size.size
}

// ---------------------------------------------------------------------------
// Order lines
// ---------------------------------------------------------------------------

/** One line of the order being put together on the page. */
export interface DraftLine {
  item_id: string
  size: string
  quantity: number
  unit_price_cents: number
}

type LineKey = Pick<DraftLine, 'item_id' | 'size'>

const sameKey = (a: LineKey, b: LineKey) => a.item_id === b.item_id && a.size === b.size

/** A quantity held to 1–10 (whole numbers). */
export function clampQuantity(quantity: number): number {
  if (!Number.isFinite(quantity)) return 1
  return Math.min(MAX_QUANTITY, Math.max(1, Math.round(quantity)))
}

export function lineTotal(line: Pick<DraftLine, 'quantity' | 'unit_price_cents'>): number {
  return line.quantity * line.unit_price_cents
}

export function orderTotal(lines: Array<Pick<DraftLine, 'quantity' | 'unit_price_cents'>>): number {
  return lines.reduce((sum, line) => sum + lineTotal(line), 0)
}

/** How many pieces the order holds (quantities added up). */
export function itemCount(lines: Array<Pick<DraftLine, 'quantity'>>): number {
  return lines.reduce((sum, line) => sum + line.quantity, 0)
}

/** What is left of the voucher; negative when the order is over it. */
export function remainingVoucher(voucherCents: number, totalCents: number): number {
  return voucherCents - totalCents
}

/** How much of the voucher the order uses, 0–100, for a progress bar. */
export function voucherUsedPercent(voucherCents: number, totalCents: number): number {
  if (voucherCents <= 0) return totalCents > 0 ? 100 : 0
  return Math.min(100, Math.max(0, (totalCents / voucherCents) * 100))
}

/**
 * Add a line: the same item and size again raises that line's quantity
 * (held to 10, keeping the line's price) instead of adding a second line.
 */
export function addLine(lines: DraftLine[], line: DraftLine): DraftLine[] {
  const index = lines.findIndex((l) => sameKey(l, line))
  if (index === -1) return [...lines, { ...line, quantity: clampQuantity(line.quantity) }]
  return lines.map((l, i) =>
    i === index ? { ...l, quantity: clampQuantity(l.quantity + line.quantity) } : l,
  )
}

/** Fold lines for the same item and size together, keeping first-seen order. */
export function mergeLines(lines: DraftLine[]): DraftLine[] {
  return lines.reduce<DraftLine[]>((merged, line) => addLine(merged, line), [])
}

export function setLineQuantity(lines: DraftLine[], key: LineKey, quantity: number): DraftLine[] {
  return lines.map((l) => (sameKey(l, key) ? { ...l, quantity: clampQuantity(quantity) } : l))
}

export function removeLine(lines: DraftLine[], key: LineKey): DraftLine[] {
  return lines.filter((l) => !sameKey(l, key))
}

/** A saved order's lines as page lines, at the prices they were saved at. */
export function linesFromOrder(order: { lines: StaffOrderLine[] } | null | undefined): DraftLine[] {
  if (!order) return []
  return mergeLines(
    order.lines.map((l) => ({
      item_id: l.item_id,
      size: l.size,
      quantity: l.quantity,
      unit_price_cents: l.unit_price_cents,
    })),
  )
}

/**
 * The lines at today's catalogue prices. A save prices the whole order from
 * the catalogue as it stands, so this is what a changed order will come to;
 * a line whose item or size has gone keeps the price it had.
 */
export function repriceLines(
  lines: DraftLine[],
  itemsById: Map<string, Pick<StaffOrderItem, 'sizes'>>,
): DraftLine[] {
  return lines.map((line) => {
    const item = itemsById.get(line.item_id)
    const price = item ? sizePrice(item, line.size) : null
    return price === null || price === line.unit_price_cents ? line : { ...line, unit_price_cents: price }
  })
}

/** True when two sets of lines hold the same items, sizes and quantities, in any order. */
export function sameLines(a: DraftLine[], b: DraftLine[]): boolean {
  if (a.length !== b.length) return false
  return a.every((line) => b.some((other) => sameKey(line, other) && other.quantity === line.quantity))
}

/** The one reason that is a prompt rather than a block: the page asks for a size. */
export const CHOOSE_SIZE_FIRST = 'Choose a size first.'

/**
 * Why "Add to order" is not available for this choice, or null when it is.
 * Short, plain sentences: they show under the button. With no size chosen
 * yet the answer is CHOOSE_SIZE_FIRST — unless even the cheapest size would
 * take the order over the voucher, which is worth saying straight away.
 */
export function addBlockedReason(args: {
  lines: DraftLine[]
  voucherCents: number
  item: StaffOrderItem
  size: string | null | undefined
  quantity: number
}): string | null {
  const { lines, voucherCents, item, size, quantity } = args
  if (item.sizes.length === 0) return 'This item is not available.'
  const total = orderTotal(lines)
  const goesOver = (price: number) => total + price * quantity > voucherCents
  const overVoucher = () =>
    `That would take your order over your voucher (${formatRand(
      Math.max(0, remainingVoucher(voucherCents, total)),
    )} left).`
  const price = size ? sizePrice(item, size) : null
  if (!size || price === null) {
    const cheapest = Math.min(...item.sizes.map((s) => s.price_cents))
    return goesOver(cheapest) ? overVoucher() : CHOOSE_SIZE_FIRST
  }
  const inOrder = lines.find((l) => sameKey(l, { item_id: item.id, size }))?.quantity ?? 0
  if (inOrder >= MAX_QUANTITY) return `You already have ${MAX_QUANTITY} of these in your order.`
  if (inOrder + quantity > MAX_QUANTITY) {
    return `You can order up to ${MAX_QUANTITY} of each item in one size.`
  }
  return goesOver(price) ? overVoucher() : null
}

// ---------------------------------------------------------------------------
// Dates and times (South African time)
// ---------------------------------------------------------------------------

let longFormat: Intl.DateTimeFormat | null = null
let shortFormat: Intl.DateTimeFormat | null = null
let partsFormat: Intl.DateTimeFormat | null = null

function partsOf(format: Intl.DateTimeFormat, date: Date): Record<string, string> {
  const out: Record<string, string> = {}
  for (const part of format.formatToParts(date)) out[part.type] = part.value
  return out
}

function readInstant(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const date = parseApiDate(iso)
  return Number.isNaN(date.getTime()) ? null : date
}

/** "21 October 2026, 23:59" in South African time; empty when unreadable. */
export function formatSast(iso: string | null | undefined): string {
  const date = readInstant(iso)
  if (!date) return ''
  if (!longFormat) longFormat = new Intl.DateTimeFormat('en-GB', {
    timeZone: SAST_TIME_ZONE,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })
  const p = partsOf(longFormat, date)
  return `${p.day} ${p.month} ${p.year}, ${p.hour}:${p.minute}`
}

/** "21 Oct 2026, 23:59" in South African time, for tables; empty when unreadable. */
export function formatSastShort(iso: string | null | undefined): string {
  const date = readInstant(iso)
  if (!date) return ''
  if (!shortFormat) shortFormat = new Intl.DateTimeFormat('en-GB', {
    timeZone: SAST_TIME_ZONE,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })
  const p = partsOf(shortFormat, date)
  return `${p.day} ${p.month.replace(/\.$/, '')} ${p.year}, ${p.hour}:${p.minute}`
}

/**
 * The South African date and time of an instant, as the values a date input
 * ("2026-10-21") and a time input ("23:59") hold. Null when unreadable.
 */
export function toSastParts(iso: string | null | undefined): { date: string; time: string } | null {
  const date = readInstant(iso)
  if (!date) return null
  if (!partsFormat) partsFormat = new Intl.DateTimeFormat('en-GB', {
    timeZone: SAST_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })
  const p = partsOf(partsFormat, date)
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` }
}

/**
 * A South African date ("2026-10-21") and time ("23:59") as an ISO string
 * with its offset ("2026-10-21T23:59:00+02:00"). South Africa keeps +02:00 all
 * year, so the offset is fixed. Null for an incomplete or impossible value.
 */
export function fromSastParts(date: string, time: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return null
  const iso = `${date}T${time}:00+02:00`
  const back = toSastParts(iso)
  // An impossible day (31 September) either fails to parse or rolls over.
  return back && back.date === date && back.time === time ? iso : null
}

/** True when the instant is still ahead of `now`. */
export function isFuture(iso: string | null | undefined, now: Date = new Date()): boolean {
  const date = readInstant(iso)
  return !!date && date.getTime() > now.getTime()
}

/**
 * Whole minutes until `iso` (rounded up, at least 1) while it is in the
 * future; null once it has passed or when unreadable.
 */
export function minutesUntil(iso: string | null | undefined, now: Date = new Date()): number | null {
  const date = readInstant(iso)
  if (!date) return null
  const ms = date.getTime() - now.getTime()
  if (ms <= 0) return null
  return Math.max(1, Math.ceil(ms / 60_000))
}

// ---------------------------------------------------------------------------
// Small text helpers
// ---------------------------------------------------------------------------

/** A campus code as its label, falling back to the code itself. */
export function campusLabel(campuses: StaffOrderCampus[], code: string | null | undefined): string {
  if (!code) return ''
  return campuses.find((c) => c.code === code)?.label ?? code
}

/**
 * The server's own wording for a failed call (FastAPI `{"detail": "..."}`,
 * written for the end user), or the fallback when there is none — a
 * validation error's detail is a list, not a sentence.
 */
export function errorDetail(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } } | null)?.response?.data?.detail
  return typeof detail === 'string' && detail.trim() ? detail : fallback
}

/** Employee numbers typed with commas, semicolons, spaces or new lines; duplicates dropped. */
export function splitEmployeeIds(text: string): string[] {
  const seen = new Set<string>()
  for (const raw of (text ?? '').split(/[\s,;]+/)) {
    const id = raw.trim()
    if (id) seen.add(id)
  }
  return [...seen]
}

/** A link safe to open: http(s) or a path on this site. Anything else is dropped. */
export function safeLink(url: string | null | undefined): string | null {
  const value = (url ?? '').trim()
  if (/^https?:\/\//i.test(value)) return value
  if (value.startsWith('/') && !value.startsWith('//')) return value
  return null
}

/** "1 item" / "3 items". */
export function piecesLabel(count: number): string {
  return `${count} ${count === 1 ? 'item' : 'items'}`
}
